import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';

import { BuilderBlocks } from '@builder.io/react';
import * as Sentry from '@sentry/nextjs';
import { useQueryClient } from '@tanstack/react-query';
import config from 'config';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { useABTest } from '@techstyle/react-abtest';
import { ImageAsset, useAsset } from '@techstyle/react-assets';
import {
  desktop,
  Grid,
  mobile,
  PaginatedGrid,
  PlaceholderGrid,
  useBreakpoint,
  useBreakpointSelector,
  useGridState,
  useLayoutEffect,
  useLoadingStatus,
  useProductBrowser,
  Video,
} from '@techstyle/react-components';
import { useApolloClient } from '@techstyle/react-graphql';
import { useProductActions } from '@techstyle/react-products';
import { VirtualScroll } from '@techstyle/react-virtual-scroll';
import { useDomain, useSession } from '@techstyle/redux-core';

import { BREAKPOINTS, CAROUSEL_BREAKPOINTS } from '../../constants/breakpoints';
import { searchFilterTracking } from '../../segment-events/utils';
import { applyByoFilters } from '../../utils/byo';
import { getCioCollectionProducts } from '../../utils/cio/cioUtils';
import getGqlProductPricing from '../../utils/getGqlProductPricing';
import isNewPricingPromoBannersFlagOn from '../../utils/isNewPricingPromoBannersFlagOn';
import { useLDFlags } from '../../utils/LD/useLDFlags';
import normalizeFilters from '../../utils/normalizeFilters';
import { loadNorthStarProducts } from '../../utils/northStarProductsV2';
import { useDefaultProductFilters } from '../../utils/productBrowser';
import { createProductsReducer, usePageSize } from '../../utils/products';
import salesFpls from '../../utils/salesFpls';
import {
  getBlankProfileSizeByCategory,
  getDefaultProductFields,
  isPreorderable as checkIsPreorderable,
} from '../../utils/selectors';
import { addAllGenderSizesToFilters } from '../../utils/sizes';
import { StepperProvider } from '../../utils/stepper';
import { useMembership } from '../../utils/useMembership';
import { useToggleCollapsibleFilters } from '../../utils/useToggleCollapsibleFilters';
import { useTrackPromotionClick } from '../../utils/useTrackPromotionClick';
import BuilderGridCarousel from '../BuilderGridCarousel';
import { useByoContext } from '../ByoProvider';
import { useCioClientContext } from '../CioClientProvider/CioClientProvider';
import { Component as EmptyGridsRecommendations } from '../EmptyGridsRecommendations';
import { TrackPromotionEnterViewport } from '../EnterViewport';
import { usePageNum } from '../GridPageNumProvider/GridPageNumProvider';
import { Component as InlineSizeCell } from '../InlineSizeCell';
import PaginationButtons from '../PaginationButtons';
import ProductCellBMIG from '../ProductCellBMIG';
import { useQuickViewContext } from '../QuickViewProvider';

const MAX_GET_ITEMS_EARLY_RETURN_COUNT = 10;

const setId = config.get('public.productTypes.setId');
const imageAspectRatio = 0.75;

const StyledPlaceholderGrid = styled(PlaceholderGrid)`
  --imageAspectRatio: ${imageAspectRatio};
  --chinHeight: 31px;
  --cellColor: transparent;
  margin-bottom: var(--rowGap);
`;

const AssetCell = styled.div`
  position: relative;
  grid-column: span ${props => props.cellsWide};
  grid-row: span ${props => props.cellsTall};

  ${mobile`
    ${props =>
      props.cellsWide === 2 && props.cellsTall === 2
        ? css`
            padding-bottom: 25px;
          `
        : ''}
  `}
`;

const InlineAssetCell = styled(AssetCell)`
  margin: 0 12px 28px;
  ${desktop`
    margin: 0 0 28px;
  `}
`;

const BuilderBlockCell = styled(AssetCell)`
  margin: 0;
`;

const BuilderBlockOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 2%;
  font-size: 24px;
  font-weight: bold;
  color: red;
  z-index: 1;
`;

const StyledBuilderBlocks = styled(BuilderBlocks)`
  height: 100%;
`;

const GridImageAsset = styled(ImageAsset)`
  width: 100%;
`;

const ProductCellWrapper = styled.div`
  position: relative;
`;

export const ProductGridProductCell = styled(ProductCellBMIG)`
  ${ProductCellBMIG.Callout} {
    margin-top: ${props => props.theme.spacing.tiny}px;
  }

  ${ProductCellBMIG.ProductSection} {
    & > *:not(:last-child) {
      margin-bottom: 0;
    }
  }

  ${ProductCellBMIG.MoreColorsLink} {
    margin-top: ${props => props.theme.spacing.tiny}px;
  }
`;

const EmptyGridsRecommendationsWrapper = styled.div`
  ${EmptyGridsRecommendations.EmptyGridRecommendationsContainer} {
    ${desktop`
      // to account for padding on parent wrapper
      width: calc(100% + ${props => props.theme.spacing.medium}px);
    `};
  }
  ${mobile`
    // to account for padding on parent ProductGridWrapper
    width: calc(100% + ${props => props.theme.spacing.medium}px);
    transform: translateX(-${props => props.theme.spacing.small}px);
  `}
`;

const GridWrapper = ({ breakpoint, carouselOptions, isCarousel, children }) => {
  if (isCarousel) {
    return (
      <BuilderGridCarousel carouselOptions={carouselOptions}>
        {children}
      </BuilderGridCarousel>
    );
  }
  return (
    <Grid
      style={{ marginBottom: breakpoint.gap }}
      data-autotag="grid"
      data-qa-automation="gridPageContent"
    >
      {children}
    </Grid>
  );
};

GridWrapper.propTypes = {
  breakpoint: PropTypes.object,
  carouselOptions: PropTypes.object,
  isCarousel: PropTypes.bool,
  children: PropTypes.node,
};

export const ProductGrid = function ({
  boostedIds,
  builderComponent,
  excludeMasterProductIds,
  featuredGridBlocks,
  filterBarEnabled,
  filterKeys,
  fplId,
  gridSlotsAsset,
  initialFilters,
  initialProducts,
  initialSortOption,
  isBuilderEditing,
  isQuickViewEnabled,
  maxItemCount,
  shouldUpdateSort,
  options,
  productPricingMap,
  productSource,
  routeParams,
  setProductPricingMap,
  shouldShowByoLayout,
  sortOptions,
  isCarousel,
  carouselOptions,
  elementsToHide,
  shouldShowLaydown,
  onProductListUpdate,
  enablePagination = false,
  constructorCollectionId,
}) {
  const gqlClient = useApolloClient();
  const queryClient = useQueryClient();
  const cioClient = useCioClientContext();
  const { isLoggedIn } = useSession();
  const currentState = useSelector(state => state);
  const { tld } = useDomain();
  const { isVip } = useMembership();
  const { asPath } = useRouter();
  const { inlineSizeAsset, inlineSizeCategory, showLaydownImages } =
    routeParams;
  // Testing an AA Test, not doing anything with this value
  // eslint-disable-next-line no-unused-vars
  const ABTest = useABTest('AAsessiongrid');

  const gridBreakpoints = useBreakpointSelector(BREAKPOINTS);
  const carouselBreakpoints = useBreakpointSelector(CAROUSEL_BREAKPOINTS);
  const isByo = productSource === 'Build Your Own';

  const {
    filters: inputFilters,
    sort,
    setSort,
    totalCount: productBrowserTotalCount,
    setTotalCount,
  } = useProductBrowser();
  const actions = useProductActions();
  const { breakpoint } = useGridState();
  const isFirstRender = useRef(true);
  const toggledCollapsibleFilters = useRef(false);
  const { isMobile } = useBreakpoint();
  const {
    northstar: northstarEnabled,
    'new-product-pricing': shouldDisplayNewProductPricing,
    'experiment-new-pricing-promo-banners': newPricingPromoBannersFlag,
  } = useLDFlags();
  // exclude /sale and /sale-secondary for NewProductPricing Experiment
  const displayNewProductPricing =
    !/\/sale/.test(asPath) &&
    (isNewPricingPromoBannersFlagOn(newPricingPromoBannersFlag)
      ? newPricingPromoBannersFlag.newPricing
      : shouldDisplayNewProductPricing);

  const pathName = new URL(`https://savagex.com${asPath}`).pathname;
  const openFilters = useToggleCollapsibleFilters({ initialFilters });
  const { pageNum, setPageNum } = usePageNum();

  const { 'constructor-grids': constructorGridsEnabled } = useLDFlags();

  useEffect(() => {
    if (!toggledCollapsibleFilters.current) {
      openFilters();
      toggledCollapsibleFilters.current = true;
    }
  }, [initialFilters, toggledCollapsibleFilters, openFilters]);

  const isSportGrid = pathName === '/products/sport';

  const shouldUseNorthStar =
    (isSportGrid && northstarEnabled) ||
    // SXF-4649 this options prop is specific to builder
    options?.productApi === 'northstar';

  const [startIndex, setStartIndex] = useState(1);
  const [lastIndex, setLastIndex] = useState(1);

  const reduxDispatch = useDispatch();

  const { startLoading } = useLoadingStatus();

  const gridAssetContainer = useAsset(gridSlotsAsset, {
    multi: true,
  });

  const dispatchTrackPromotionClick = useTrackPromotionClick();

  const filters = useDefaultProductFilters(inputFilters, options);

  const customFilterForSale = useMemo(
    () =>
      salesFpls({
        excludeFpls: filters.excludeFpls,
        tld,
        path: asPath,
        isVip,
      }),
    [asPath, isVip, tld, filters]
  );

  // If it's Builder and there are automatic sales FPLs (it's a sales page),
  // merges those FPLs with the FPLs configured in Builder.
  if (builderComponent && customFilterForSale.fpls) {
    filters.fpls = [...new Set([...filters.fpls, ...customFilterForSale.fpls])];
    customFilterForSale.fpls = filters.fpls;
  }

  const payload = useMemo(() => {
    const appendedFilters = addAllGenderSizesToFilters(
      // Normalization is important because filters are used to build
      // caching keys (see `payloadKey` and TanStack Query's
      // `queryClient.getQueryData` below). Also because it transforms a
      // `product_category_id_list` filter with a composite id like
      // `['1611,1931']` into `['1611', '1931']`, which is the format required
      // by the backend.
      normalizeFilters(filters)
    );
    return {
      ...(shouldShowByoLayout
        ? applyByoFilters(appendedFilters)
        : appendedFilters),
      sort: sort?.sort,
      sortKey: sort?.value,
      ...customFilterForSale,
    };
  }, [customFilterForSale, filters, shouldShowByoLayout, sort]);

  const prevFilterSnapShot = useRef({
    filter: payload.aggregationFilter,
    sort: initialSortOption.sort,
    sortKey: initialSortOption.value,
  });

  const payloadKey = useMemo(() => JSON.stringify(payload), [payload]);

  const [state, dispatch] = useReducer(createProductsReducer(payloadKey), {
    payloadKey: initialProducts ? payloadKey : undefined,
    pages: {
      [enablePagination ? pageNum : 1]: initialProducts
        ? initialProducts.products
        : undefined,
    },
    totalCount: initialProducts ? initialProducts.total : undefined,
  });

  const { openQuickViewModal, setDefaultProductWithSelections } =
    useQuickViewContext();
  const { isInterstitialFlow } = useByoContext();

  const { pages, totalCount } = state;
  const isStale = payloadKey !== state.payloadKey;

  useLayoutEffect(() => {
    if (
      !isFirstRender.current &&
      !isStale &&
      typeof onProductListUpdate === 'function'
    ) {
      onProductListUpdate();
    }
  }, [isStale, onProductListUpdate]);

  useEffect(() => {
    if (totalCount !== productBrowserTotalCount) {
      setTotalCount(totalCount);
    }
  }, [totalCount, setTotalCount, productBrowserTotalCount]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
    }
  }, []);

  const slots = useMemo(() => {
    const getSlotSize = (cellsWide, cellsTall) => {
      const width =
        cellsWide === 'full' ? gridBreakpoints?.columns || 4 : cellsWide;
      const height = cellsTall && !isNaN(cellsTall) ? cellsTall : 1;
      const result = width * height;
      return !isNaN(result) ? result : 0;
    };

    const assets = (gridAssetContainer?.data?.assets || []).map(asset => {
      const { customVars } = asset.options;
      const cellsWide = customVars.cellsWide || customVars.size;
      const cellsTall = customVars.cellsTall;

      return {
        asset,
        itemType: 'asset',
        position: customVars.gridPosition,
        row: customVars.row,
        column: customVars.column,
        cellsWide,
        cellsTall,
        minColumns: customVars.minColumns,
        maxColumns: customVars.maxColumns,
        slotSize: getSlotSize(cellsWide, cellsTall),
      };
    });

    const blocks = (featuredGridBlocks || []).map((block, i) => {
      const blockWidth =
        isMobile && block.widthMobile ? block.widthMobile : block.width;
      const blockHeight =
        isMobile && block.heightMobile ? block.heightMobile : block.height;
      const cellsWide = Math.min(gridBreakpoints?.columns || 4, blockWidth);
      const cellsTall =
        blockWidth >= (gridBreakpoints?.columns || 4) ? 1 : blockHeight;

      return {
        block,
        position: +block.position,
        itemType: 'builderBlock',
        cellsWide,
        cellsTall,
        slotSize: getSlotSize(cellsWide, cellsTall),
        index: i,
      };
    });
    return [...assets, ...blocks];
  }, [featuredGridBlocks, gridAssetContainer, gridBreakpoints, isMobile]);

  const blankProfileSizes = useSelector(state =>
    getBlankProfileSizeByCategory(state, { category: inlineSizeCategory })
  );

  const setDefaultProductAndOpenQuickViewModal = useCallback(
    (product, isRegularSet) => async () => {
      await setDefaultProductWithSelections(product, isRegularSet, true);
      openQuickViewModal();
    },
    [openQuickViewModal, setDefaultProductWithSelections]
  );

  const { pageSize } = usePageSize();

  // use less dependencies on the builder editor to improve performance
  // when values are changed often

  const loadDeps = useMemo(
    () =>
      isBuilderEditing
        ? [
            startLoading,
            state.payloadKey,
            enablePagination,
            payloadKey,
            pages,
            boostedIds,
            excludeMasterProductIds,
            actions,
            productSource,
            reduxDispatch,
            filterKeys,
            pageSize,
          ]
        : [
            gqlClient,
            queryClient,
            shouldUseNorthStar,
            startLoading,
            state.payloadKey,
            payloadKey,
            pages,
            sort,
            sortOptions,
            boostedIds,
            builderComponent,
            payload,
            excludeMasterProductIds,
            actions,
            productSource,
            reduxDispatch,
            filterKeys,
            pageSize,
            // Constructor
            cioClient,
            constructorCollectionId,
            constructorGridsEnabled,
            setProductPricingMap,
          ],
    [
      actions,
      boostedIds,
      builderComponent,
      excludeMasterProductIds,
      filterKeys,
      gqlClient,
      queryClient,
      isBuilderEditing,
      pages,
      payload,
      payloadKey,
      productSource,
      reduxDispatch,
      enablePagination,
      shouldUseNorthStar,
      sort,
      sortOptions,
      startLoading,
      state.payloadKey,
      pageSize,
      // Constructor
      cioClient,
      constructorCollectionId,
      constructorGridsEnabled,
      setProductPricingMap,
    ]
  );

  const loadItems = useCallback(async ({ startIndex, stopIndex }) => {
    const stopLoading = startLoading();
    const startPage = Math.floor(startIndex / pageSize) + 1;
    const stopPage = Math.floor((stopIndex - 1) / pageSize) + 1;
    const requests = [];

    for (let page = startPage; page <= stopPage; page++) {
      const hasPage = state.payloadKey === payloadKey && pages[page] != null;
      if (!hasPage) {
        // using boostedIds to display 'pair it with' products of previous product on the top
        // and that only happens when we are in the first page and sort is default in byo grids
        const isDefaultSort = !sort || sort.sort === sortOptions[0].sort;
        const shouldEnableBoostedIds =
          boostedIds && boostedIds.length && isDefaultSort && startPage === 1;
        const params = {
          page,
          size: pageSize,
          boostedIds: shouldEnableBoostedIds ? boostedIds : [],
          ...payload,
          // NOTE: restricts product fields returned in order to
          // decrease payload size from API
          productFields: getDefaultProductFields(),
          // For Builder components, `excludeMasterProductIds`
          // is in `payload`, it's not passed as a parameter.
          // Without the condition, the undefined parameter value would
          // override the value in the `payload`.
          ...(builderComponent ? {} : { excludeMasterProductIds }),
        };

        if (constructorGridsEnabled) {
          const queryResult = await queryClient.fetchQuery({
            // eslint-disable-next-line @tanstack/query/exhaustive-deps
            queryKey: ['cio-collection', payloadKey, page],
            queryFn: () =>
              getCioCollectionProducts({
                ...params,
                cioClient,
                collectionId: constructorCollectionId,
                isVip: false,
              }),
            staleTime: Infinity,
          });

          // Note that if `queryResult.products` is empty, `fetchQuery` will
          // return `undefined`.
          const data = await queryClient.fetchQuery({
            // eslint-disable-next-line @tanstack/query/exhaustive-deps
            queryKey: ['product-pricing', queryResult.products],
            queryFn: () =>
              getGqlProductPricing({
                apolloClient: gqlClient,
                setProductPricingMap,
                products: queryResult.products,
              }),
            staleTime: Infinity,
          });
          dispatch({
            type: 'LOAD_PRODUCTS',
            payload: [
              {
                ...queryResult,
                products: data?.productsWithPricing,
              },
            ],
            payloadKey,
          });
        } else if (shouldUseNorthStar) {
          requests.push(loadNorthStarProducts(gqlClient, params));
        } else {
          // Check if the request is already cached, if so, use the cached request.
          // Otherwise, make the request and cache it using the queryClient.
          const cachedRequest = queryClient.getQueryData(['products', params]);
          if (cachedRequest) {
            requests.push(cachedRequest);
          } else {
            requests.push(
              actions
                .loadProducts(params, { apiVersion: 2, productSource })
                .then(response => {
                  queryClient.setQueryData(
                    ['products', params],
                    response.payload
                  );
                  return response.payload;
                })
            );
          }
        }
      }
    }

    try {
      searchFilterTracking(reduxDispatch, {
        filterKeys: filterKeys,
        list: productSource,
        category: payload.backgroundCategoryIds,
        prevSort: prevFilterSnapShot.current.sortKey,
        currentSort: payload.sortKey,
        prevFilters: prevFilterSnapShot.current.filter,
        currentFilters: payload.aggregationFilter,
      });

      prevFilterSnapShot.current = {
        filter: payload.aggregationFilter,
        sort: payload.sort,
        sortKey: payload.sortKey,
      };

      if (!constructorGridsEnabled) {
        const payloads = await Promise.all(requests);

        dispatch({
          type: 'LOAD_PRODUCTS',
          payload: payloads,
          payloadKey,
        });
      }
    } catch (err) {
      Sentry.captureException(err);
    } finally {
      stopLoading();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, loadDeps);

  const renderItems = useCallback(
    ({ isVisible, items, startIndex, stopIndex, lastKnownHeight }) => {
      let stopLoading;
      try {
        // Only render the batch if it contains products, not just assets
        if (isVisible && items) {
          if (isBuilderEditing) {
            stopLoading = startLoading();
          }
          let gridItems = items;
          let slotsPreviousToCurrentPage = 0;
          let blockOffset = 0;

          const shouldShowInlineSizeGrid =
            isLoggedIn &&
            blankProfileSizes.length > 0 &&
            inlineSizeAsset &&
            !isCarousel &&
            startIndex === 0;

          if (shouldShowInlineSizeGrid && gridBreakpoints) {
            // Display the inline size grid after the Second row
            const withInlineSize = [...items];
            withInlineSize.splice(2 * gridBreakpoints.columns, 0, {
              isInlineSize: true,
              cellsWide: gridBreakpoints.columns,
            });

            gridItems = withInlineSize;
          } else if (startIndex > 0) {
            // since subsequent pages wont take into account assets/blocks
            // we need to add the space occupied by these for the overlay
            slots.forEach(slot => {
              slotsPreviousToCurrentPage += slot.slotSize || 0;
            });
          }

          return (
            <GridWrapper
              carouselOptions={carouselOptions}
              isCarousel={isCarousel}
              breakpoint={breakpoint}
            >
              {gridItems.map((item, index) => {
                const isPreorderable = checkIsPreorderable(currentState, {
                  product: item,
                });
                const isBuildYourOwnPage = productSource === 'Build Your Own';
                if (isBuildYourOwnPage && isPreorderable) {
                  return;
                }

                const showPositionNumbers =
                  builderComponent &&
                  isBuilderEditing &&
                  builderComponent.component.options.showBlockPositions;

                const positionOverlay = (
                  <BuilderBlockOverlay>
                    {index +
                      startIndex +
                      1 +
                      slotsPreviousToCurrentPage +
                      blockOffset}
                  </BuilderBlockOverlay>
                );

                if (item.slotSize) {
                  blockOffset += item.slotSize - 1;
                }

                if (item.isInlineSize) {
                  // substract to ignore the inline assets on positioning
                  blockOffset -= 1;
                  return (
                    <InlineAssetCell
                      key="inline-grid-size"
                      cellsWide={item.cellsWide}
                    >
                      <StepperProvider>
                        <InlineSizeCell
                          blankSizes={blankProfileSizes}
                          initialFilters={initialFilters}
                        />
                      </StepperProvider>
                    </InlineAssetCell>
                  );
                }

                if (item.itemType === 'builderBlock' && builderComponent) {
                  const { block, index: blockIndex } = item;
                  return (
                    <BuilderBlockCell
                      key={blockIndex}
                      cellsWide={item.cellsWide}
                      cellsTall={item.cellsTall}
                    >
                      {showPositionNumbers && positionOverlay}
                      <StyledBuilderBlocks
                        parentElementId={
                          builderComponent && builderComponent.id
                        }
                        dataPath={`component.options.gridOptions.blocks.${blockIndex}.content`}
                        child
                        blocks={block.content || block}
                      />
                    </BuilderBlockCell>
                  );
                }

                if (item.itemType === 'asset') {
                  const { customVars } = item.asset.options;

                  const videoId =
                    customVars.videoUrl ||
                    customVars.videoId ||
                    customVars.VideoID;

                  return (
                    <TrackPromotionEnterViewport
                      key={`asset-${item.asset.id}`}
                      asset={item.asset}
                    >
                      <AssetCell
                        cellsWide={item.cellsWide}
                        cellsTall={item.cellsTall}
                        onClick={() => dispatchTrackPromotionClick(item)}
                      >
                        {showPositionNumbers && positionOverlay}
                        {videoId ? (
                          <Video
                            aspectRatio={1.4}
                            url={Video.getUrlFromVideoId(videoId)}
                          />
                        ) : (
                          <GridImageAsset asset={item.asset} />
                        )}
                      </AssetCell>
                    </TrackPromotionEnterViewport>
                  );
                }

                return (
                  <ProductCellWrapper
                    key={item.masterProductId}
                    data-qa-automation="gridItemUpdated"
                  >
                    {showPositionNumbers && positionOverlay}
                    <ProductGridProductCell
                      shouldShowLaydown={shouldShowLaydown || showLaydownImages}
                      elementsToHide={elementsToHide}
                      data-autotag="grid-product"
                      data-qa-automation="gridItem"
                      fplId={fplId}
                      gridIndex={startIndex + index}
                      imageAspectRatio={imageAspectRatio}
                      isByo={isByo}
                      key={item.masterProductId}
                      payloadKey={payloadKey}
                      product={item}
                      productSource={productSource}
                      shouldShowByoLayout={shouldShowByoLayout}
                      isQuickViewEnabled={isQuickViewEnabled}
                      setDefaultProductAndOpenQuickViewModal={setDefaultProductAndOpenQuickViewModal(
                        item,
                        setId === item.id
                      )}
                      shouldDisplayNewProductPricing={displayNewProductPricing}
                    />
                  </ProductCellWrapper>
                );
              })}
            </GridWrapper>
          );
        }
      } catch (err) {
        Sentry.captureException(err);
      } finally {
        if (isBuilderEditing && stopLoading) {
          stopLoading();
        }
      }

      if (isCarousel) {
        return (
          <StyledPlaceholderGrid
            columns={carouselBreakpoints?.columns}
            itemCount={carouselBreakpoints?.columns}
            imageAspectRatio={imageAspectRatio}
            totalHeight={lastKnownHeight}
          />
        );
      }
      return (
        <StyledPlaceholderGrid
          itemCount={stopIndex - startIndex}
          imageAspectRatio={imageAspectRatio}
          totalHeight={lastKnownHeight}
        />
      );
    },
    [
      isCarousel,
      isBuilderEditing,
      isLoggedIn,
      blankProfileSizes,
      inlineSizeAsset,
      gridBreakpoints,
      carouselOptions,
      breakpoint,
      startLoading,
      slots,
      currentState,
      productSource,
      builderComponent,
      shouldShowLaydown,
      showLaydownImages,
      elementsToHide,
      fplId,
      isByo,
      payloadKey,
      shouldShowByoLayout,
      isQuickViewEnabled,
      setDefaultProductAndOpenQuickViewModal,
      displayNewProductPricing,
      initialFilters,
      dispatchTrackPromotionClick,
      carouselBreakpoints?.columns,
    ]
  );

  // Escape hatch implemented to avoid infinite loop
  const getItemsEarlyReturnCount = useRef(0);

  const getItems = useCallback(
    ({ startIndex, stopIndex }) => {
      // TODO? Some sort of throttle applied to loadItems retries
      if (state.payloadKey !== payloadKey) {
        return null;
      }
      const startPage = Math.floor(startIndex / pageSize) + 1;
      let startOffset = startIndex % pageSize;
      const stopPage = Math.floor((stopIndex - 1) / pageSize) + 1;
      const items = [];
      for (let page = startPage; page <= stopPage; page++) {
        const pageItems = pages[page];
        if (pageItems) {
          const batchItems = startOffset
            ? pageItems.slice(startOffset)
            : pageItems;
          items.push(...batchItems);
        } else {
          // Don't have all the pages yet, bail out!
          return null;
        }
        startOffset = 0;
      }

      const length = stopIndex - startIndex;

      // set values for pagination label
      setStartIndex(startIndex + 1);
      setLastIndex(stopIndex);

      // ! Careful - this can trigger an infinite loop (via loadItems) depending on
      // ! the truthiness evaluation. This should fail more gracefully.
      if (
        items.length < length &&
        !isCarousel &&
        getItemsEarlyReturnCount.current <= MAX_GET_ITEMS_EARLY_RETURN_COUNT
      ) {
        getItemsEarlyReturnCount.current++;
        return null; // Don't have expected number of items, return nothing!
      }

      if (items.length > length) {
        items.length = length; // Truncate.
      }

      getItemsEarlyReturnCount.current = 0;

      return items;
    },
    [enablePagination, isCarousel, pageNum, pages, payloadKey, state.payloadKey]
  );

  const getTotalSlotsOffset = useCallback(() => {
    if (!slots || !slots.length) {
      return 0;
    }

    return (
      slots
        // ignore full row assets, since they don't
        // reduce the amount of items displayed on a page
        .filter(
          slot =>
            slot.cellsWide < gridBreakpoints?.columns &&
            slot.cellsWide !== 'full'
        )
        .reduce((accum, curr) => accum + (curr.slotSize || 0), 0)
    );
  }, [gridBreakpoints, slots]);

  const getTotalCount = useCallback(() => {
    if (isInterstitialFlow) {
      return 50; // Hard coding a limit of 50 for the BYO Interstitial grid
    }
    return totalCount === 0 && isStale ? null : maxItemCount || totalCount;
  }, [isInterstitialFlow, isStale, totalCount, maxItemCount]);

  const totalPages = useMemo(() => {
    // for pagination, we consider the total slots offset as well
    // in case we need to add an extra page at the end
    return Math.ceil((getTotalCount() + getTotalSlotsOffset()) / pageSize);
  }, [getTotalCount, getTotalSlotsOffset]);

  useEffect(() => {
    // shouldUpdateSort will be true if we detected that the customer has not selected any sort
    // and there is an update to the sort options. If that's the case, then we select
    // the first option, which is the Default
    if (shouldUpdateSort) {
      setSort(sortOptions[0]);
    }
  }, [shouldUpdateSort, setSort, sortOptions]);

  return (
    <>
      {totalCount === 0 && !isStale ? null : enablePagination && !isCarousel ? (
        <PaginatedGrid
          itemCount={getTotalCount()}
          targetBatchSize={pageSize}
          stepSize={breakpoint.columns}
          getItems={getItems}
          loadItems={loadItems}
          renderItems={renderItems}
          slots={slots}
          currentPage={pageNum}
        />
      ) : (
        <VirtualScroll
          itemCount={getTotalCount()}
          targetBatchSize={pageSize}
          stepSize={breakpoint.columns}
          getItems={getItems}
          loadItems={loadItems}
          renderItems={renderItems}
          slots={slots}
        />
      )}
      {!shouldShowByoLayout && totalCount <= 10 ? (
        <EmptyGridsRecommendationsWrapper>
          <EmptyGridsRecommendations
            productsLength={totalCount}
            filters={filters.aggregationFilter}
          />
        </EmptyGridsRecommendationsWrapper>
      ) : null}
      {enablePagination && totalCount > pageSize && (
        <PaginationButtons
          currentPage={pageNum}
          onPageChange={setPageNum}
          totalPages={totalPages}
          totalCount={getTotalCount()}
          startIndex={startIndex}
          lastIndex={lastIndex}
          pageSize={pageSize}
        />
      )}
    </>
  );
};

ProductGrid.propTypes = {
  boostedIds: PropTypes.array,
  builderComponent: PropTypes.object,
  excludeMasterProductIds: PropTypes.array,
  featuredGridBlocks: PropTypes.array,
  filterBarEnabled: PropTypes.bool,
  filterKeys: PropTypes.array,
  fplId: PropTypes.number,
  gridSlotsAsset: PropTypes.string,
  initialFilters: PropTypes.object,
  initialProducts: PropTypes.shape({
    products: PropTypes.array,
    total: PropTypes.number,
  }),
  initialSortOption: PropTypes.object,
  isBuilderEditing: PropTypes.bool,
  isQuickViewEnabled: PropTypes.bool,
  options: PropTypes.object,
  productSource: PropTypes.string,
  routeParams: PropTypes.object,
  shouldShowByoLayout: PropTypes.bool,
  shouldUpdateSort: PropTypes.bool,
  carouselOptions: PropTypes.object,
  isCarousel: PropTypes.bool,
  elementsToHide: PropTypes.object,
  maxItemCount: PropTypes.number,
  sortOptions: PropTypes.arrayOf(
    PropTypes.shape({
      sort: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(
          PropTypes.shape({
            field: PropTypes.string,
            direction: PropTypes.string,
          })
        ),
      ]),
    })
  ),
};
